import { createSignal } from 'solid-js'
import type { EventFormData } from '../domain/event-form.schema'

interface SectionToggle {
  isEnabled: (id: string) => boolean
  toggle: (id: string, enabled: boolean) => void
  setActive: (id: string) => void
  activeSection: () => string
}

/** Derive initial enabled state from form values — a section is enabled if any of its key fields are populated. */
const deriveInitialState = (iv: Partial<EventFormData>): Record<string, boolean> => ({
  ticket_sales: !!(iv.ticketUrl || iv.ticketButtonText),
  early_access: !!iv.rsvpButtonText,
  presale: !!(iv.presaleCode || iv.presaleStart),
  vip: !!(iv.vipButtonText || iv.vipUrl),
  // Add-ons key off the availability window, not the button text: the fan site gates Meet & Greet
  // on start/end, and hundreds of legacy events carry a leftover button text with no window at all.
  // Treating those as enabled made the form unsatisfiable — start/end are required once it is on.
  addons: !!(iv.meetAndGreetStart || iv.meetAndGreetEnd)
})

export const createSectionToggle = (initialValues: Partial<EventFormData>): SectionToggle => {
  const [enabled, setEnabled] = createSignal<Record<string, boolean>>(deriveInitialState(initialValues))
  const [activeSection, setActiveSection] = createSignal('general')

  const isEnabled = (id: string): boolean => enabled()[id] ?? true

  const toggle = (id: string, value: boolean): void => {
    setEnabled((prev) => ({ ...prev, [id]: value }))
  }

  return { isEnabled, toggle, setActive: setActiveSection, activeSection }
}
